import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Chip,
  Box,
  Typography,
  IconButton,
  Autocomplete
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ko } from 'date-fns/locale';
import { Close, FilterList, Clear } from '@mui/icons-material';
import { SearchFilters } from '../types';

interface UserFilterProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: SearchFilters) => void;
  onClear: () => void;
  currentFilters: SearchFilters;
  availableOptions: {
    headquarters: string[];
    departments: string[];
    positions: string[];
    locations: string[];
    assignments: string[];
  };
}

const UserFilter: React.FC<UserFilterProps> = ({
  open,
  onClose,
  onApply,
  onClear,
  currentFilters,
  availableOptions
}) => {
  const [filters, setFilters] = useState<SearchFilters>(currentFilters);

  // 다이얼로그가 열릴 때 현재 필터 상태로 초기화
  useEffect(() => {
    if (open) {
      setFilters(currentFilters);
    }
  }, [open, currentFilters]);

  // 필터 값 변경 핸들러
  const handleFilterChange = (field: keyof SearchFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 필터 적용
  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  // 필터 초기화
  const handleClear = () => {
    const emptyFilters: SearchFilters = {
      search: '',
      headquarters: '',
      department: '',
      position: '',
      assignments: '',
      location: '',
      initialEducationStartDate: '',
      initialEducationEndDate: '',
      recentEducationStartDate: '',
      recentEducationEndDate: ''
    };
    setFilters(emptyFilters);
    onClear();
    onClose();
  };

  // 활성 필터 개수 계산
  const getActiveFilterCount = () => {
    return Object.values(filters).filter(value => value.trim() !== '').length;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <FilterList />
            <Typography variant="h6">
              고급 필터
            </Typography>
            {getActiveFilterCount() > 0 && (
              <Chip 
                size="small" 
                label={`${getActiveFilterCount()}개 적용`}
                color="primary" 
              />
            )}
          </Box>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* 검색어 */}
          <Box>
            <TextField
              fullWidth
              label="검색어"
              placeholder="이름, 사번 등으로 검색..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              helperText="이름, 사번, 부서명, 직위, 직책에서 검색됩니다"
            />
          </Box>

          {/* 필터 옵션들 */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
            <Autocomplete
              options={availableOptions.headquarters}
              value={filters.headquarters}
              onChange={(_, newValue) => handleFilterChange('headquarters', newValue || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="본부"
                  placeholder="본부 선택..."
                />
              )}
              freeSolo
            />
            
            <Autocomplete
              options={availableOptions.departments}
              value={filters.department}
              onChange={(_, newValue) => handleFilterChange('department', newValue || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="부서"
                  placeholder="부서 선택..."
                />
              )}
              freeSolo
            />
            
            <Autocomplete
              options={availableOptions.positions}
              value={filters.position}
              onChange={(_, newValue) => handleFilterChange('position', newValue || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="직위"
                  placeholder="직위 선택..."
                />
              )}
              freeSolo
            />
            
            <Autocomplete
              options={availableOptions.assignments}
              value={filters.assignments}
              onChange={(_, newValue) => handleFilterChange('assignments', newValue || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="발령 사항"
                  placeholder="발령 사항 선택..."
                />
              )}
              freeSolo
            />
            
            <Autocomplete
              options={availableOptions.locations}
              value={filters.location}
              onChange={(_, newValue) => handleFilterChange('location', newValue || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="지점/공항"
                  placeholder="지점/공항 선택..."
                />
              )}
              freeSolo
            />
          </Box>

          {/* 초기 보안교육일 필터 */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              초기 보안교육일 필터
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <DatePicker
                label="시작일"
                value={filters.initialEducationStartDate ? new Date(filters.initialEducationStartDate) : null}
                onChange={(date) => handleFilterChange('initialEducationStartDate', date ? date.toISOString().split('T')[0] : '')}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small'
                  }
                }}
              />
              <DatePicker
                label="종료일"
                value={filters.initialEducationEndDate ? new Date(filters.initialEducationEndDate) : null}
                onChange={(date) => handleFilterChange('initialEducationEndDate', date ? date.toISOString().split('T')[0] : '')}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small'
                  }
                }}
              />
            </Box>
          </Box>

          {/* 정기 보안교육일 필터 */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              정기 보안교육일 필터
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <DatePicker
                label="시작일"
                value={filters.recentEducationStartDate ? new Date(filters.recentEducationStartDate) : null}
                onChange={(date) => handleFilterChange('recentEducationStartDate', date ? date.toISOString().split('T')[0] : '')}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small'
                  }
                }}
              />
              <DatePicker
                label="종료일"
                value={filters.recentEducationEndDate ? new Date(filters.recentEducationEndDate) : null}
                onChange={(date) => handleFilterChange('recentEducationEndDate', date ? date.toISOString().split('T')[0] : '')}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small'
                  }
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* 현재 적용된 필터 미리보기 */}
        {getActiveFilterCount() > 0 && (
          <Box mt={3}>
            <Typography variant="subtitle2" gutterBottom>
              적용될 필터:
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {filters.search && (
                <Chip
                  size="small"
                  label={`검색: ${filters.search}`}
                  onDelete={() => handleFilterChange('search', '')}
                />
              )}
              {filters.headquarters && (
                <Chip
                  size="small"
                  label={`본부: ${filters.headquarters}`}
                  onDelete={() => handleFilterChange('headquarters', '')}
                />
              )}
              {filters.department && (
                <Chip
                  size="small"
                  label={`부서: ${filters.department}`}
                  onDelete={() => handleFilterChange('department', '')}
                />
              )}
              {filters.position && (
                <Chip
                  size="small"
                  label={`직위: ${filters.position}`}
                  onDelete={() => handleFilterChange('position', '')}
                />
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleClear}
          startIcon={<Clear />}
          disabled={getActiveFilterCount() === 0}
        >
          초기화
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose}>
          취소
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          startIcon={<FilterList />}
        >
          적용
        </Button>
      </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default UserFilter;
